import React from "react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Drop-in SEO component for every key page.
 *
 * Mirrors the canonical metadata in /public/index.html for the home
 * page, then overrides title / description / canonical / OG image on a
 * per-route basis so Google + social crawlers that DO execute JS (e.g.
 * Googlebot) see route-specific previews. Static social crawlers
 * (WhatsApp, Facebook, LinkedIn, X, Telegram, iMessage) read the
 * index.html copy directly — those still display the brand defaults,
 * which is intentional (consistent brand preview when the homepage URL
 * is shared, route-aware previews after JS execution).
 *
 * All three locales (en / fr / es) are emitted as <link rel="alternate"
 * hreflang> so search engines surface the right translation per market.
 */

const SITE_URL = "https://visitxaluca.com";
const DEFAULT_OG_IMAGE =
  "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/6pvzp897_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-1.jpg";
const BRAND = "Grup Xaluca";

const LOCALE_TAG = { en: "en_US", fr: "fr_FR", es: "es_ES" };

const SEO = ({
  title,
  description,
  path = "/",
  image,
  type = "website",
  noindex = false,
}) => {
  const { lang } = useLanguage();
  // Note: <html lang="…"> is set imperatively by LanguageContext (see
  // its useEffect). We deliberately do NOT emit it from Helmet here to
  // avoid a race where Helmet re-emits a stale/empty value after the
  // context has already committed the new one.
  const fullTitle = title ? `${title} · ${BRAND}` : `${BRAND} · The Soul of Southern Morocco`;
  const url = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_OG_IMAGE;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}

      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="fr" href={url} />
      <link rel="alternate" hrefLang="es" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:locale" content={LOCALE_TAG[lang] || "en_US"} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@grupxaluca" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={fullTitle} />
    </Helmet>
  );
};

export default SEO;
